import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDraft } from '../../contexts/DraftContext';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  ShoppingCart as OrderIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Visibility as VisibilityIcon,
  TouchApp as TouchAppIcon,
} from '@mui/icons-material';
import {
  customerCRMAPI,
  CustomerCRMBrief,
  CustomerCRMFull,
} from '../../services/customerCrmAPI';
import api from '../../services/api';
import CustomerPlannerContext from './CustomerPlannerContext';
import VoiceRecorder from '../../components/VoiceRecorder';
import CRMSubNav from './CRMSubNav';
import CustomerBillingPanel from '../../components/CustomerBillingPanel';
import ConversationTimeline from '../../components/ConversationTimeline';

interface CustomerEmail {
  id: number;
  email_type: string;
  email_type_display: string;
  subject: string;
  status: string;
  sent_at: string;
  opened_at: string | null;
  opened_count: number;
  clicked_at: string | null;
  order_number: string | null;
}

// Caller assignments
const CALLER_ASSIGNMENTS: Record<string, string[]> = {
  'Brad': ['WHOLESALE_FARM', 'RETAIL_PARTNER', 'LOCAL_WILL_CALL', 'OTHER', 'CJ_CUSTOMER'],
  'Bekah': ['RETAIL_PARTNER'],
  'Sibyl': ['WHOLESALE_FARM', 'RETAIL_PARTNER', 'LOCAL_WILL_CALL', 'OTHER', 'CJ_CUSTOMER'],
  'Zion': ['WHOLESALE_FARM'],
};

const CALLERS = ['Brad', 'Bekah', 'Sibyl', 'Zion'];

const CUSTOMER_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'WHOLESALE_FARM', label: 'Wholesale (Farms)' },
  { value: 'RETAIL_PARTNER', label: 'Retail Partners' },
  { value: 'LOCAL_WILL_CALL', label: 'Local Will Call' },
  { value: 'OTHER', label: 'Other' },
  { value: 'CJ_CUSTOMER', label: 'CJ Customer' },
];

const CustomerCRM: React.FC = () => {
  // State
  const [selectedCaller, setSelectedCaller] = useState<string>('');
  const [selectedCustomerType, setSelectedCustomerType] = useState<string>('');
  // /crm/customers/:id preselects, so a row in the Call Log can link straight through to
  // the customer it belongs to. The route existed but the id was never read.
  const { id: routeCustomerId } = useParams<{ id?: string }>();
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    routeCustomerId ? Number(routeCustomerId) : null,
  );
  const [customers, setCustomers] = useState<CustomerCRMBrief[]>([]);
  const [customerData, setCustomerData] = useState<CustomerCRMFull | null>(null);
  const [customerEmails, setCustomerEmails] = useState<CustomerEmail[]>([]);
  const [notes, setNotes] = useState<string>('');
  // Draft persistence for the in-progress call notes (a create form — no server
  // baseline, so restore is always benign). Keyed by the selected customer.
  const { hasDraft, getDraft, setDraftField, clearDraft } = useDraft();
  const [notesRestored, setNotesRestored] = useState(false);
  useEffect(() => {
    if (routeCustomerId && Number(routeCustomerId) !== selectedCustomerId) {
      setSelectedCustomerId(Number(routeCustomerId));
    }
    // selectedCustomerId is deliberately not a dependency: this syncs FROM the url, and
    // including it would fight the picker when someone selects a different customer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCustomerId]);

  const notesDraftKey = selectedCustomerId ? `crm-call:${selectedCustomerId}` : '';
  const commitNotes = (v: string) => {
    setNotes(v);
    if (notesDraftKey) setDraftField(notesDraftKey, 'notes', v);
  };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Get allowed customer types based on selected caller
  const allowedCustomerTypes = useMemo(() => {
    if (!selectedCaller) return CUSTOMER_TYPE_OPTIONS;
    const allowed = CALLER_ASSIGNMENTS[selectedCaller] || [];
    return CUSTOMER_TYPE_OPTIONS.filter(
      opt => opt.value === '' || allowed.includes(opt.value)
    );
  }, [selectedCaller]);

  // Filter customers based on caller and type
  const filteredCustomers = useMemo(() => {
    let filtered = customers;

    // Filter by customer type
    if (selectedCustomerType) {
      filtered = filtered.filter(c => c.customer_type === selectedCustomerType);
    }

    // Filter by caller assignment
    if (selectedCaller) {
      const allowed = CALLER_ASSIGNMENTS[selectedCaller] || [];
      filtered = filtered.filter(c => allowed.includes(c.customer_type));
    }

    return filtered;
  }, [customers, selectedCaller, selectedCustomerType]);

  // Load customers on mount
  useEffect(() => {
    loadCustomers();
  }, []);

  // Reset customer type if not allowed for caller
  useEffect(() => {
    if (selectedCaller && selectedCustomerType) {
      const allowed = CALLER_ASSIGNMENTS[selectedCaller] || [];
      if (!allowed.includes(selectedCustomerType)) {
        setSelectedCustomerType('');
      }
    }
  }, [selectedCaller, selectedCustomerType]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerCRMAPI.getAll();
      setCustomers(response.data.results);
    } catch (err) {
      console.error('Failed to load customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleRetrieve = async () => {
    if (!selectedCustomerId) {
      setError('Please select a customer');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load customer data and email history in parallel
      const [customerResponse, emailsResponse] = await Promise.all([
        customerCRMAPI.getById(selectedCustomerId),
        api.get(`/admin/emails/customer/${selectedCustomerId}/`).catch(() => ({ data: { emails: [] } })),
      ]);

      setCustomerData(customerResponse.data);
      setCustomerEmails(emailsResponse.data.emails || []);
      // Restore any in-progress call notes for this customer; else start empty.
      const key = `crm-call:${selectedCustomerId}`;
      if (hasDraft(key)) {
        setNotes(getDraft(key)!.fields.notes || '');
        setNotesRestored(true);
      } else {
        setNotes('');
        setNotesRestored(false);
      }
    } catch (err) {
      console.error('Failed to load customer:', err);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogConversation = async () => {
    if (!selectedCustomerId || !selectedCaller) {
      setError('Please select a caller and customer');
      return;
    }

    if (!notes.trim()) {
      setError('Please enter notes from the call');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await customerCRMAPI.logInteraction(selectedCustomerId, {
        caller: selectedCaller,
        notes: notes.trim(),
        interaction_type: 'PHONE_CALL',
      });

      setSuccess('Conversation logged successfully!');
      setNotes('');
      if (notesDraftKey) clearDraft(notesDraftKey);   // saved — draft done
      setNotesRestored(false);

      // Refresh customer data
      const response = await customerCRMAPI.getById(selectedCustomerId);
      setCustomerData(response.data);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to log conversation:', err);
      setError('Failed to log conversation');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PROSPECT': return 'info';
      case 'INACTIVE': return 'default';
      case 'ON_HOLD': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <CRMSubNav />
      {/* Header */}
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#1e5e1e' }}>
        Customer CRM - Call Logging
      </Typography>

      {/* Top Bar - Filters & Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center'
        }}>
          {/* Who is calling */}
          <Box sx={{ minWidth: 150, flex: '1 1 150px', maxWidth: 200 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Who is calling</InputLabel>
              <Select
                value={selectedCaller}
                label="Who is calling"
                onChange={(e) => setSelectedCaller(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select caller</em>
                </MenuItem>
                {CALLERS.map((caller) => (
                  <MenuItem key={caller} value={caller}>
                    {caller}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Customer Type */}
          <Box sx={{ minWidth: 150, flex: '1 1 150px', maxWidth: 200 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Customer Type</InputLabel>
              <Select
                value={selectedCustomerType}
                label="Customer Type"
                onChange={(e) => setSelectedCustomerType(e.target.value)}
              >
                {allowedCustomerTypes.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Customer Select */}
          <Box sx={{ flex: '2 1 300px', maxWidth: 400 }}>
            <Autocomplete
              options={filteredCustomers}
              getOptionLabel={(option) => `${option.name} - ${option.city}, ${option.state}`}
              value={filteredCustomers.find(c => c.id === selectedCustomerId) || null}
              onChange={(_, newValue) => setSelectedCustomerId(newValue?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customer"
                  size="small"
                  placeholder="Search customers..."
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.customer_type_display} | {option.city}, {option.state}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Box>

          {/* Retrieve Button */}
          <Box sx={{ minWidth: 120 }}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={handleRetrieve}
              disabled={!selectedCustomerId || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            >
              Retrieve
            </Button>
          </Box>

          {/* Log Conversation Button */}
          <Box sx={{ minWidth: 120 }}>
            <Button
              variant="contained"
              sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
              fullWidth
              onClick={handleLogConversation}
              disabled={!customerData || !selectedCaller || !notes.trim() || saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              Log Call
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      {customerData && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3
        }}>
          {/* Left Column - Customer Info & Conversation History */}
          <Box>
            {/* Farm Intelligence Panel - from Planner */}
            <CustomerPlannerContext customerId={selectedCustomerId!} customerType={customerData.customer_type} />

            {/* Customer Info Panel */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: '#1e5e1e' }} />
                <Typography variant="h6" fontWeight="bold">
                  Customer Information
                </Typography>
                <Chip
                  label={customerData.crm_profile?.account_status || 'ACTIVE'}
                  color={getStatusColor(customerData.crm_profile?.account_status || 'ACTIVE') as any}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {customerData.name}
                  </Typography>
                  <Chip
                    label={customerData.customer_type_display}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Contact Person
                    </Typography>
                    <Typography variant="body1">{customerData.contact_person}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body1">{customerData.phone}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body1">{customerData.email || 'N/A'}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <LocationIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary', mt: 0.5 }} />
                  <Box>
                    <Typography variant="body1">{customerData.address}</Typography>
                    <Typography variant="body1">
                      {customerData.city}, {customerData.state} {customerData.postal_code}
                    </Typography>
                  </Box>
                </Box>

                {/* Payment Terms */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Terms
                    </Typography>
                    <Chip
                      label={customerData.payment_terms_display || 'Due on Receipt'}
                      size="small"
                      color={customerData.payment_terms !== 'due_on_receipt' ? 'success' : 'default'}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Credit / Balance
                    </Typography>
                    <Typography
                      variant="body1"
                      color={customerData.is_over_credit_limit ? 'error' : 'text.primary'}
                      fontWeight={customerData.is_over_credit_limit ? 'bold' : 'normal'}
                    >
                      ${(customerData.current_balance || 0).toLocaleString()}
                      {customerData.credit_limit && (
                        <span style={{ color: '#666', fontWeight: 'normal' }}>
                          {' '}/ ${customerData.credit_limit.toLocaleString()}
                        </span>
                      )}
                      {customerData.is_over_credit_limit && (
                        <span style={{ color: '#d32f2f', marginLeft: 4 }}>(Over limit!)</span>
                      )}
                    </Typography>
                  </Box>
                </Box>

                {customerData.crm_profile?.preferred_products && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Preferred Products
                    </Typography>
                    <Typography variant="body1">
                      {customerData.crm_profile.preferred_products}
                    </Typography>
                  </Box>
                )}

                {customerData.crm_profile?.special_pricing_notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Special Pricing/Terms
                    </Typography>
                    <Typography variant="body1">
                      {customerData.crm_profile.special_pricing_notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Conversation History */}
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HistoryIcon sx={{ mr: 1, color: '#1e5e1e' }} />
                <Typography variant="h6" fontWeight="bold">
                  Conversation History
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {customerData.recent_interactions.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No previous conversations recorded
                </Typography>
              ) : (
                <ConversationTimeline
                  interactions={customerData.recent_interactions}
                  labelFirst="Last Conversation"
                  labelSecond="2nd to Last Conversation"
                />
              )}
            </Paper>
          </Box>

          {/* Right Column - Order History & Notes Input */}
          <Box>
            {/* Order History */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <OrderIcon sx={{ mr: 1, color: '#1e5e1e' }} />
                <Typography variant="h6" fontWeight="bold">
                  Order History
                </Typography>
                <Chip
                  label={`${customerData.total_orders} total orders`}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              {customerData.recent_orders.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No orders found
                </Typography>
              ) : (
                customerData.recent_orders.map((order, index) => (
                  <Card
                    key={order.id}
                    variant="outlined"
                    sx={{
                      mb: 2,
                      bgcolor: index === 0 ? '#e3f2fd' : '#f5f5f5',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {index === 0 ? 'Last Order' : '2nd to Last Order'}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {formatCurrency(order.total_amount)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Order #{order.order_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(order.order_date)}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        {order.items.slice(0, 3).map((item, i) => (
                          <Typography key={i} variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {item.quantity}x {item.formula_name}
                          </Typography>
                        ))}
                        {order.items.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            ... and {order.items.length - 3} more items
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Paper>

            {/* Invoices + Payments + AR summary (shared component, also used on Customers.tsx Billing tab) */}
            <Box sx={{ mb: 3 }}>
              <CustomerBillingPanel
                invoices={customerData.invoices || []}
                payments={customerData.payments || []}
                arSummary={customerData.ar_summary}
                returnPath={`/crm/customers/${customerData.id}`}
              />
            </Box>

            {/* Email History */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MarkEmailReadIcon sx={{ mr: 1, color: '#1e5e1e' }} />
                <Typography variant="h6" fontWeight="bold">
                  Email History
                </Typography>
                <Chip
                  label={`${customerEmails.length} emails`}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              {customerEmails.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No emails sent to this customer
                </Typography>
              ) : (
                customerEmails.slice(0, 5).map((email, index) => (
                  <Card
                    key={email.id}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      bgcolor: index === 0 ? '#f3e5f5' : '#f5f5f5',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                          {email.subject.length > 40 ? email.subject.substring(0, 40) + '...' : email.subject}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                          {email.opened_count > 0 && (
                            <Chip
                              icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                              label={email.opened_count > 1 ? `${email.opened_count}x` : 'Opened'}
                              size="small"
                              color="secondary"
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                            />
                          )}
                          {email.clicked_at && (
                            <Chip
                              icon={<TouchAppIcon sx={{ fontSize: 14 }} />}
                              label="Clicked"
                              size="small"
                              color="info"
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          {email.email_type_display}
                          {email.order_number && ` - Order #${email.order_number}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(email.sent_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
              {customerEmails.length > 5 && (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ textAlign: 'center', mt: 1, cursor: 'pointer' }}
                  onClick={() => window.open(`/admin/emails?customer_id=${selectedCustomerId}`, '_blank')}
                >
                  View all {customerEmails.length} emails →
                </Typography>
              )}
            </Paper>

            {/* Notes Input */}
            <Paper
              sx={{
                p: 2,
                bgcolor: '#e8f5e9',
                border: '2px solid #4caf50',
              }}
            >
              {notesRestored && (
                <Box role="status" sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                  mb: 1, p: 1, borderRadius: 1, bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
                  color: '#1e40af', fontSize: 13,
                }}>
                  <span>Restored your unsaved call notes.</span>
                  <Button size="small" onClick={() => {
                    setNotes('');
                    if (notesDraftKey) clearDraft(notesDraftKey);
                    setNotesRestored(false);
                  }}>Discard</Button>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#1e5e1e' }}>
                  Notes from Today's Call
                </Typography>
                <VoiceRecorder
                  endpoint="/api/voice/transcribe/crm/"
                  compact
                  summarize
                  additionalData={selectedCustomerId ? { customer_id: String(selectedCustomerId) } : undefined}
                  onTranscriptionComplete={(result) => {
                    // Append transcription (+ any action items) to notes, persisting the draft.
                    const transcribedText = result.text;
                    let combined = notes
                      ? `${notes}\n\n[Voice Note]: ${transcribedText}`
                      : `[Voice Note]: ${transcribedText}`;
                    if (result.action_items && result.action_items.length > 0) {
                      const actionItemsText = result.action_items.map(item => `- ${item}`).join('\n');
                      combined += `\n\n[Action Items]:\n${actionItemsText}`;
                    }
                    commitNotes(combined);
                  }}
                />
              </Box>
              <TextField
                multiline
                rows={8}
                fullWidth
                placeholder="Enter notes from your conversation with this customer... (or use voice recording)"
                value={notes}
                onChange={(e) => commitNotes(e.target.value)}
                sx={{
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#4caf50',
                    },
                    '&:hover fieldset': {
                      borderColor: '#2e7d32',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e5e1e',
                    },
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Click "Log Call" to save these notes. Current caller: <strong>{selectedCaller || 'Not selected'}</strong>
              </Typography>
            </Paper>
          </Box>
        </Box>
      )}

      {/* Empty State */}
      {!customerData && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a caller and customer, then click "Retrieve" to view their information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Caller assignments: Bekah (Retail only), Zion (Wholesale only), Brad & Sibyl (All)
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CustomerCRM;
